import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useSession } from "@/lib/auth-client";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

// Fires a toast once on mount, then redirects. `toast()` can't run during
// render, so it lives in an effect; the stable id dedupes StrictMode's
// double-invoke and avoids stacking toasts across rapid forbidden hits.
function ForbiddenRedirect() {
  const { t } = useTranslation();
  useEffect(() => {
    toast.error(t("errors.forbiddenPage"), { id: "forbidden-page" });
  }, [t]);
  return <Navigate to="/" replace />;
}

export default function ProtectedRoute({
  children,
  allowedRoles,
}: ProtectedRouteProps) {
  const { data: session, isPending } = useSession();

  if (isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const role = (session.user as { role?: string })?.role;
  if (allowedRoles && role && !allowedRoles.includes(role)) {
    return <ForbiddenRedirect />;
  }

  return <>{children}</>;
}
